import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, DollarSign, X, AlertCircle, Search, Calendar, User, Package, Building2, FileText } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import SearchableUserDropdown from './SearchableUserDropdown';
import StyledDropdown from './StyledDropdown';
import PdfPreviewModal from './PdfPreviewModal';
import { generateSalesComparisonPDF, generateSalesReportPDF } from '../utils/pdfGenerator';

const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [productDetailsList, setProductDetailsList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEnrollmentPopup, setShowEnrollmentPopup] = useState(false);
  const [saleForEnrollment, setSaleForEnrollment] = useState(null);
  const [enrollmentFormData, setEnrollmentFormData] = useState({ bankName: '', bankAccountNumber: '', startDate: '', endDate: '', jointName: '' });
  const [enrollmentSubmitting, setEnrollmentSubmitting] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Export PDF (preview-first, mobile share sheet)
  const [exporting, setExporting] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfDownloadName, setPdfDownloadName] = useState('sales-report.pdf');
  const [pdfModalTitle, setPdfModalTitle] = useState('Sales report preview');

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfPreviewOpen(false);
  };

  const handleExportPDF = async () => {
    setShowExportConfirm(false);
    setExporting(true);

    try {
      if (!sales || sales.length === 0) {
        alert('No sales records to export.');
        return;
      }

      const monthName = months.find((m) => m.value === filterMonth)?.label || '';
      const period = `${monthName} ${filterYear}`;
      const hasCompare = !!compareGroup1 && !!compareGroup2;

      const salesData = (sales || []).map((s) => ({
        date: s.date,
        user: s.user?.name || 'N/A',
        product: s.product?.name || 'N/A',
        amount: s.amount,
        group: s.user?.group?.name || 'Unassigned',
      }));

      let url;
      if (hasCompare) {
        const idA = parseInt(compareGroup1, 10);
        const idB = parseInt(compareGroup2, 10);
        if (idA === idB) {
          alert('Please select two different groups to compare.');
          return;
        }

        const groupNameA = groups.find((g) => g.id === idA)?.name || 'Unassigned';
        const groupNameB = groups.find((g) => g.id === idB)?.name || 'Unassigned';

        url = await generateSalesComparisonPDF(salesData, period, groupNameA, groupNameB);
      } else {
        const groupByGroup = !filterUser && !filterProduct;
        url = await generateSalesReportPDF(salesData, period, 'Sales Summary Report', { groupByGroup });
      }

      const slug = `${filterMonth}-${filterYear}`;
      setPdfDownloadName(hasCompare ? `sales-comparison-${slug}.pdf` : `sales-summary-${slug}.pdf`);
      setPdfModalTitle(hasCompare ? 'Sales comparison preview' : 'Sales report preview');
      setPdfPreviewUrl(url);
      setPdfPreviewOpen(true);
    } catch (err) {
      console.error('Export sales report error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = () => {
    // Keep the UX consistent with Commission Management:
    // if a specific user is selected, export immediately; otherwise show confirmation popup.
    if (filterUser) handleExportPDF();
    else setShowExportConfirm(true);
  };

  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterGroup, setFilterGroup] = useState('');
  const [compareGroup1, setCompareGroup1] = useState('');
  const [compareGroup2, setCompareGroup2] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  const [formData, setFormData] = useState({
    userId: '',
    productId: '',
    investorId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    groupId: '',
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      if (filterUser) params.append('userId', filterUser);
      if (filterProduct) params.append('productId', filterProduct);
      if (filterGroup) params.append('groupId', filterGroup);

      const [salesRes, usersRes, productsRes, groupsRes, investorsRes, productDetailsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/sales?${params.toString()}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/sales/users/list`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/sales/products/list`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/investors/all`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/investors/products`, { headers: getAuthHeaders() }),
      ]);

      const salesResult = await salesRes.json();
      const usersResult = await usersRes.json();
      const productsResult = await productsRes.json();
      const groupsResult = await groupsRes.json();
      const investorsResult = await investorsRes.json();
      const productDetailsResult = await productDetailsRes.json();

      if (salesResult.success) setSales(salesResult.data);
      if (usersResult.success) setUsers(usersResult.data);
      if (productsResult.success) setProducts(productsResult.data);
      if (groupsResult.success) setGroups(groupsResult.data);
      if (investorsResult.success) {
        const approved = (investorsResult.data || []).filter((i) => i.status === 'Approved' || i.status === 'Active');
        setInvestors(approved);
      }
      if (productDetailsResult.success) setProductDetailsList(productDetailsResult.data || []);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterMonth, filterYear, filterGroup, filterUser, filterProduct]);

  const resetForm = () => {
    setFormData({
      userId: '',
      productId: '',
      investorId: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      groupId: '',
    });
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale recorded successfully');
        setShowCreateModal(false);
        const saleData = result.data;
        const hadInvestor = !!formData.investorId;
        resetForm();
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
        if (hadInvestor && saleData) {
          setSaleForEnrollment({
            saleId: saleData.id,
            investorId: formData.investorId,
            productId: saleData.product?.id || formData.productId,
            userId: saleData.user?.id || formData.userId,
            amount: saleData.amount ?? formData.amount,
            date: saleData.date || formData.date,
          });
          const startDate = formData.date || new Date().toISOString().split('T')[0];
          const pd = productDetailsList.find((p) => p.productId === parseInt(saleData.product?.id || formData.productId) || p.product?.id === parseInt(formData.productId));
          let endDate = '';
          if (pd?.tenureMonths && startDate) {
            const d = new Date(startDate);
            d.setMonth(d.getMonth() + pd.tenureMonths);
            endDate = d.toISOString().split('T')[0];
          }
          setEnrollmentFormData({
            bankName: '',
            bankAccountNumber: '',
            startDate: startDate,
            endDate: endDate,
            jointName: '',
          });
          setShowEnrollmentPopup(true);
        }
      } else {
        setError(result.message || 'Failed to record sale');
      }
    } catch (err) {
      console.error('Create sale error:', err);
      setError('Failed to record sale');
    }
  };

  const handleUpdateSale = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales/${editingSale.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale updated successfully');
        setShowEditModal(false);
        setEditingSale(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to update sale');
      }
    } catch (err) {
      console.error('Update sale error:', err);
      setError('Failed to update sale');
    }
  };

  const handleDeleteSale = async (id) => {
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale deleted successfully');
        setDeleteConfirm(null);
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to delete sale');
      }
    } catch (err) {
      console.error('Delete sale error:', err);
      setError('Failed to delete sale');
    }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    setFormData({
      userId: sale.userId,
      productId: sale.productId.toString(),
      investorId: sale.investorId || '',
      amount: parseFloat(sale.amount).toString(),
      date: new Date(sale.date).toISOString().split('T')[0],
      description: sale.description || '',
      groupId: sale.groupId ? String(sale.groupId) : '',
    });
    setShowEditModal(true);
  };

  const openViewModal = (sale) => {
    setViewingSale(sale);
    setShowViewModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  const formatNumber = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-MY').format(num);
  };

  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSelectedProduct = () => {
    return products.find(p => p.id === parseInt(formData.productId));
  };

  const getSelectedUser = () => users.find((u) => u.id === formData.userId);

  const getEffectiveRates = () => {
    const product = getSelectedProduct();
    if (!product) return null;
    const gid = formData.groupId ? parseInt(formData.groupId, 10) : null;
    const override = gid && product.groupCommissions
      ? product.groupCommissions.find((o) => o.groupId === gid)
      : null;
    return {
      agentComm: override ? override.agentComm : product.agentComm,
      teamLeaderComm: override ? override.teamLeaderComm : product.teamLeaderComm,
      groupLeaderComm: override ? override.groupLeaderComm : product.groupLeaderComm,
    };
  };

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options dynamically
  // From 2020 (or system start year) to current year + 1 (for future planning)
  const currentYear = new Date().getFullYear();
  const startYear = 2020; // System start year
  const endYear = currentYear + 1; // Allow next year for planning
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);

  // Filter users by selected group
  const filteredUsers = filterGroup
    ? users.filter(user => user.group?.id === parseInt(filterGroup))
    : users;

  const salesSortedByDateDesc = useMemo(() => {
    return [...(sales || [])].sort((a, b) => {
      const ta = new Date(a.date).getTime();
      const tb = new Date(b.date).getTime();
      if (tb !== ta) return tb - ta;
      return b.id - a.id;
    });
  }, [sales]);

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedSales,
    totalItems,
    handlePageChange,
    resetPage,
  } = usePagination(salesSortedByDateDesc, 10);

  // Reset page when filters change
  useEffect(() => {
    resetPage();
  }, [filterMonth, filterYear, filterGroup, filterUser, filterProduct]);

  if (loading && sales.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Sales Management</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">Record and manage sales transactions</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 neo-btn-primary px-4 py-2"
          >
            <Plus size={20} />
            Record Sale
          </button>
          <button
            onClick={handleExportClick}
            disabled={exporting}
            className="flex items-center gap-2 neo-btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export PDF"
          >
            <FileText size={20} />
            {exporting ? 'Generating...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 transition-colors duration-200">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 transition-colors duration-200">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="neo-raised p-4 mb-6 transition-colors duration-200">
        <div className="flex flex-wrap gap-4">
          {/* Month Filter */}
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
              value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />

          {/* Year Filter */}
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
              value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />

          {/* Group Filter */}
          <StyledDropdown
            options={[
              { value: '', label: 'All Groups' },
              ...groups.filter(g => g.status === 'Active').map(g => ({ value: g.id.toString(), label: g.name }))
            ]}
              value={filterGroup}
            onChange={(val) => {
                setCompareGroup1('');
                setCompareGroup2('');
                setFilterGroup(val);
                setFilterUser(''); // Reset user filter when group changes
              }}
            placeholder="All Groups"
            icon={Building2}
            showClear={true}
              disabled={!!compareGroup1 || !!compareGroup2}
            className="min-w-[180px]"
          />

          {/* Compare Group 1 */}
          <StyledDropdown
            options={groups
              .filter((g) => g.status === 'Active')
              .map((g) => ({ value: g.id.toString(), label: g.name }))}
            value={compareGroup1}
            onChange={(val) => {
              setCompareGroup1(val);
              setCompareGroup2('');
              setFilterGroup('');
              setFilterUser('');
            }}
            placeholder="Compare Group 1"
            icon={Building2}
            showClear={true}
            className="min-w-[210px]"
          />

          {/* Compare Group 2 */}
          <StyledDropdown
            options={groups
              .filter((g) => g.status === 'Active')
              .map((g) => ({ value: g.id.toString(), label: g.name }))}
            value={compareGroup2}
            onChange={(val) => {
              setCompareGroup2(val);
              setFilterGroup('');
              setFilterUser('');
            }}
            placeholder="Compare Group 2"
            icon={Building2}
            showClear={true}
            className="min-w-[210px]"
            disabled={!compareGroup1}
          />

          {/* User Filter */}
          <div className="flex items-center gap-2">
            <User size={20} className="text-gray-400 dark:text-dark-muted" />
            <SearchableUserDropdown
              users={filteredUsers}
              value={filterUser}
              onChange={setFilterUser}
              placeholder={`All Users${filterGroup ? ' in Group' : ''}`}
              maxResults={50}
            />
          </div>

          {/* Product Filter */}
          <StyledDropdown
            options={[
              { value: '', label: 'All Products' },
              ...products.map(p => ({ value: p.id.toString(), label: p.name }))
            ]}
              value={filterProduct}
            onChange={setFilterProduct}
            placeholder="All Products"
            icon={Package}
            showClear={true}
            className="min-w-[300px]"
          />
        </div>
      </div>

      {/* Sales Table */}
      <div className="neo-raised overflow-hidden transition-colors duration-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Sales Person
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Product
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Recorded By
                </th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {sales.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted">
                    <DollarSign size={48} className="mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                    <p className="text-lg font-medium dark:text-dark-text">No sales records found</p>
                    <p className="text-sm dark:text-dark-muted">Start by recording a sale</p>
                  </td>
                </tr>
              ) : (
                paginatedSales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      {formatDate(sale.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{sale.user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-muted">{sale.user.role.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      {sale.product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-dark-text">
                      {formatCurrency(sale.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-muted">
                      {sale.recorder.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openViewModal(sale)}
                          className="text-primary-600 hover:text-primary-800 p-1"
                          title="View Details"
                        >
                          <Search size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(sale)}
                          className="text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text p-1 transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(sale)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Summary */}
      {sales.length > 0 && (
        <div className="mt-4 neo-raised p-4 transition-colors duration-200">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-dark-muted">Total Sales ({sales.length} records)</span>
            <span className="text-xl font-bold text-gray-900 dark:text-dark-text">
              {formatCurrency(sales.reduce((sum, s) => sum + parseFloat(s.amount), 0))}
            </span>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="neo-modal w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Record New Sale</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateSale} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Sales Person *
                </label>
                <SearchableUserDropdown
                  users={users}
                  value={formData.userId}
                  onChange={(val) => {
                    const selected = users.find((u) => u.id === val);
                    const userGroups = selected?.groups || [];
                    setFormData({
                      ...formData,
                      userId: val,
                      groupId: userGroups.length === 1
                        ? String(userGroups[0].id)
                        : (selected?.groupId ? String(selected.groupId) : ''),
                    });
                  }}
                  placeholder="Select User"
                  maxResults={50}
                  className="w-full"
                />
              </div>
              {(getSelectedUser()?.groups || []).length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Group *
                  </label>
                  <StyledDropdown
                    options={[
                      { value: '', label: 'Select group' },
                      ...(getSelectedUser()?.groups || []).map((g) => ({ value: String(g.id), label: g.name })),
                    ]}
                    value={formData.groupId}
                    onChange={(val) => setFormData({ ...formData, groupId: val })}
                    placeholder="Select group"
                    className="w-full"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Product *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select Product' },
                    ...products.map(p => ({ value: p.id.toString(), label: p.name }))
                  ]}
                  value={formData.productId}
                  onChange={(val) => setFormData({ ...formData, productId: val })}
                  placeholder="Select Product"
                  icon={Package}
                  className="w-full"
                />
              </div>

              {/* Show product commission info */}
              {getSelectedProduct() && getEffectiveRates() && (
                <div className="neo-inset p-3 text-sm transition-colors duration-200">
                  <div className="font-medium text-gray-700 dark:text-dark-text mb-2">Commission Rates:</div>
                  <div className="grid grid-cols-3 gap-2 text-gray-600 dark:text-dark-muted">
                    <div>Agent: {parseFloat(getEffectiveRates().agentComm).toFixed(2)}%</div>
                    <div>TL: {parseFloat(getEffectiveRates().teamLeaderComm).toFixed(2)}%</div>
                    <div>GL: {parseFloat(getEffectiveRates().groupLeaderComm).toFixed(2)}%</div>
                  </div>
                  {(getSelectedProduct().tlOverrideThreshold || getSelectedProduct().glOverrideThreshold) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-dark-border">
                      <div className="font-medium text-gray-700 dark:text-dark-text mb-1">Thresholds:</div>
                      <div className="text-gray-600 dark:text-dark-muted">
                        {getSelectedProduct().tlOverrideThreshold && (
                          <div>TL Override: {formatCurrency(getSelectedProduct().tlOverrideThreshold)}</div>
                        )}
                        {getSelectedProduct().glOverrideThreshold && (
                          <div>GL Override: {formatCurrency(getSelectedProduct().glOverrideThreshold)}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.amount)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({ ...formData, amount: rawValue });
                    }
                  }}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., 500,000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Investor *
                </label>
                <StyledDropdown
                  options={[{ value: '', label: 'Select investor' }, ...investors.map((i) => ({ value: i.id, label: `${i.name} — ${i.email}` }))]}
                  value={formData.investorId}
                  onChange={(id) => setFormData({ ...formData, investorId: id })}
                  placeholder="Select investor"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200"
                  rows="3"
                  placeholder="Optional notes..."
                />
              </div>

              {/* Commission Preview */}
              {formData.amount && formData.productId && formData.userId && (
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 transition-colors duration-200">
                  <div className="font-medium text-primary-800 dark:text-primary-300 mb-2">Commission Preview:</div>
                  <div className="space-y-1 text-sm text-primary-700 dark:text-primary-300">
                    {(() => {
                      const product = getEffectiveRates();
                      const amount = parseFloat(formData.amount);
                      if (!product || !amount) return null;
                      // Rates are stored as percentages (e.g., 2.5 means 2.5%), divide by 100 for calculation
                      return (
                        <>
                          <div className="flex justify-between">
                            <span>Personal (Agent):</span>
                            <span>{formatCurrency(amount * parseFloat(product.agentComm) / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>TL Override:</span>
                            <span>{formatCurrency(amount * parseFloat(product.teamLeaderComm) / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>GL Override:</span>
                            <span>{formatCurrency(amount * parseFloat(product.groupLeaderComm) / 100)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 neo-btn-primary"
                >
                  Record Sale
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="neo-modal w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Edit Sale #{editingSale.id}</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSale(null);
                }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateSale} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Sales Person *
                </label>
                <SearchableUserDropdown
                  users={users}
                  value={formData.userId}
                  onChange={(val) => {
                    const selected = users.find((u) => u.id === val);
                    const userGroups = selected?.groups || [];
                    setFormData({
                      ...formData,
                      userId: val,
                      groupId: userGroups.length === 1
                        ? String(userGroups[0].id)
                        : (selected?.groupId ? String(selected.groupId) : ''),
                    });
                  }}
                  placeholder="Select User"
                  maxResults={50}
                  className="w-full"
                />
              </div>
              {(getSelectedUser()?.groups || []).length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Group *
                  </label>
                  <StyledDropdown
                    options={[
                      { value: '', label: 'Select group' },
                      ...(getSelectedUser()?.groups || []).map((g) => ({ value: String(g.id), label: g.name })),
                    ]}
                    value={formData.groupId}
                    onChange={(val) => setFormData({ ...formData, groupId: val })}
                    placeholder="Select group"
                    className="w-full"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Product *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select Product' },
                    ...products.map(p => ({ value: p.id.toString(), label: p.name }))
                  ]}
                  value={formData.productId}
                  onChange={(val) => setFormData({ ...formData, productId: val })}
                  placeholder="Select Product"
                  icon={Package}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.amount)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({ ...formData, amount: rawValue });
                    }
                  }}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., 500,000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Investor *
                </label>
                <StyledDropdown
                  options={[{ value: '', label: 'Select investor' }, ...investors.map((i) => ({ value: i.id, label: `${i.name} — ${i.email}` }))]}
                  value={formData.investorId}
                  onChange={(id) => setFormData({ ...formData, investorId: id })}
                  placeholder="Select investor"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  rows="3"
                  placeholder="Optional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSale(null);
                  }}
                  className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 neo-btn-primary"
                >
                  Update Sale
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enrollment popup after Record Sale (when Investor was selected) */}
      {showEnrollmentPopup && saleForEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="neo-modal w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Create enrollment for this sale</h2>
              <button type="button" onClick={() => { setShowEnrollmentPopup(false); setSaleForEnrollment(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const pid = saleForEnrollment.productId && (typeof saleForEnrollment.productId === 'number' ? saleForEnrollment.productId : parseInt(saleForEnrollment.productId, 10));
                const productDetail = productDetailsList.find((p) => (p.productId || (p.product && p.product.id)) === pid);
                if (!productDetail) {
                  setError('Product details not found for this product.');
                  return;
                }
                setEnrollmentSubmitting(true);
                setError('');
                try {
                  const saleDate = typeof saleForEnrollment.date === 'string' ? saleForEnrollment.date : new Date(saleForEnrollment.date).toISOString().split('T')[0];
                  const res = await fetch(`${getApiUrl()}/api/investor-enrollments`, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      investorId: saleForEnrollment.investorId,
                      productDetailsId: productDetail.id,
                      agentId: saleForEnrollment.userId,
                      bankName: enrollmentFormData.bankName,
                      bankAccountNumber: enrollmentFormData.bankAccountNumber,
                      amount: parseFloat(saleForEnrollment.amount),
                      startDate: enrollmentFormData.startDate,
                      endDate: enrollmentFormData.endDate || undefined,
                      paymentReceivedDate: saleDate,
                      status: 'Active',
                      fromSaleId: saleForEnrollment.saleId,
                      jointName: enrollmentFormData.jointName?.trim() || undefined,
                    }),
                  });
                  const result = await res.json();
                  if (result.success) {
                    setSuccess('Enrollment created.');
                    setShowEnrollmentPopup(false);
                    setSaleForEnrollment(null);
                    setTimeout(() => setSuccess(''), 3000);
                  } else {
                    setError(result.message || 'Failed to create enrollment');
                  }
                } catch (err) {
                  console.error(err);
                  setError('Failed to create enrollment');
                } finally {
                  setEnrollmentSubmitting(false);
                }
              }}
              className="p-4 space-y-4"
            >
              <p className="text-sm text-gray-600 dark:text-dark-muted">Fill in the details below to create an investor enrollment for this sale.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Joint name (optional)</label>
                <input type="text" value={enrollmentFormData.jointName} onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, jointName: e.target.value })} className="w-full neo-field dark:text-dark-text px-3 py-2" placeholder="Second person's name (record only)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Bank name *</label>
                <input type="text" value={enrollmentFormData.bankName} onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, bankName: e.target.value })} className="w-full neo-field dark:text-dark-text px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Account number *</label>
                <input type="text" value={enrollmentFormData.bankAccountNumber} onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, bankAccountNumber: e.target.value })} className="w-full neo-field dark:text-dark-text px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Start date *</label>
                <input type="date" value={enrollmentFormData.startDate} onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, startDate: e.target.value })} className="w-full neo-field dark:text-dark-text px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">End date (auto from product tenure)</label>
                <input type="date" value={enrollmentFormData.endDate} onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, endDate: e.target.value })} className="w-full neo-field dark:text-dark-text px-3 py-2" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowEnrollmentPopup(false); setSaleForEnrollment(null); }} className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text">Skip</button>
                <button type="submit" disabled={enrollmentSubmitting} className="flex-1 px-4 py-2 neo-btn-primary disabled:opacity-50">{enrollmentSubmitting ? 'Creating...' : 'Create enrollment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="neo-modal w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Sale Details #{viewingSale.id}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingSale(null);
                }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Date</div>
                  <div className="font-medium dark:text-dark-text">{formatDate(viewingSale.date)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Amount</div>
                  <div className="font-medium text-lg dark:text-dark-text">{formatCurrency(viewingSale.amount)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Sales Person</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.user.name}</div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">{viewingSale.user.role.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Product</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.product.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Investor</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.investor?.name ?? '—'}</div>
                </div>
              </div>

              {viewingSale.description && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Description</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.description}</div>
                </div>
              )}

              <div className="border-t dark:border-dark-border pt-4">
                <div className="text-sm font-medium text-gray-700 dark:text-dark-text mb-3">Commission Records</div>
                <div className="space-y-2">
                  {viewingSale.commissionRecords.map((comm) => (
                    <div
                      key={comm.id}
                      className="flex justify-between items-center neo-inset p-3 transition-colors duration-200"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-dark-text">{comm.user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-muted">
                          {comm.commissionType} ({(parseFloat(comm.commissionRate) * 100).toFixed(2)}%)
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-primary-600 dark:text-primary-400">
                          {formatCurrency(comm.commissionAmount)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-sm text-gray-500 dark:text-dark-muted pt-2 border-t dark:border-dark-border">
                Recorded by {viewingSale.recorder.name} on{' '}
                {new Date(viewingSale.createdAt).toLocaleString()}
              </div>

              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingSale(null);
                }}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="neo-modal w-full max-w-md mx-4 p-6 transition-colors duration-200">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <AlertCircle size={24} />
              <h2 className="text-lg font-semibold dark:text-dark-text">Delete Sale</h2>
            </div>
            <p className="text-gray-600 dark:text-dark-muted mb-2">
              Are you sure you want to delete this sale record?
            </p>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-4">
              Sale #{deleteConfirm.id} - {formatCurrency(deleteConfirm.amount)} by{' '}
              {deleteConfirm.user.name}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">
              This will also delete all associated commission records.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSale(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Confirmation Modal */}
      {showExportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="neo-modal max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
              Generate Summary Report?
            </h3>
            <p className="text-gray-600 dark:text-dark-muted mb-4">
              {compareGroup1 && compareGroup2 ? (
                <>
                  You are about to generate a <strong>Sales Comparison Report</strong> for{' '}
                  <strong>{groups.find(g => g.id === parseInt(compareGroup1))?.name || 'Group 1'}</strong> and{' '}
                  <strong>{groups.find(g => g.id === parseInt(compareGroup2))?.name || 'Group 2'}</strong>. The report will be side-by-side in landscape.
                  Since you are not selecting a specific user, this export will use the chosen month/year.
                </>
              ) : !filterProduct && !filterGroup ? (
                <>
                  You are about to generate a <strong>Sales Summary Report</strong> for <strong>all groups</strong> with sales in the selected period. The report will be grouped by group.
                  Since you are not selecting a specific user, this export will use the chosen month/year.
                </>
              ) : (
                <>
                  You are about to generate a <strong>Sales Summary Report</strong> for the selected filters in the chosen period.
                  Since you are not selecting a specific user, this export will use the chosen month/year.
                </>
              )}
            </p>
            <p className="text-sm text-primary-600 dark:text-primary-400 mb-6">
              A single PDF opens in a preview window. Use Save / Download to save the file (on phones this opens the share sheet, including Save to Files).
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 neo-btn-primary"
              >
                {compareGroup1 && compareGroup2 ? 'Generate Comparison' : 'Generate Summary'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        pdfUrl={pdfPreviewUrl}
        title={pdfModalTitle}
        downloadFilename={pdfDownloadName}
        onClose={closePdfPreview}
      />
    </div>
  );
};

export default SalesManagement;

